import { RefObject, useEffect, useRef } from 'react';

import type HighchartsReact from 'highcharts-react-official';

import { LineDrawStyle } from '../../../types.ts';
import { getLineSeriesStyle } from '../../defaultHighChartOptions.ts';
import { referenceLineId } from './linearHighChart.constants.ts';

/**
 * Applies the draw style to the existing series, so a change keeps the zoom and the data.
 * Returns a ref with the current style for code that builds series.
 */
export const useLineDrawStyle = ({
  drawStyle,
  highChartsRef,
}: {
  drawStyle: LineDrawStyle | undefined;
  highChartsRef: RefObject<HighchartsReact.RefObject | null>;
}) => {
  const drawStyleRef = useRef(drawStyle);

  useEffect(() => {
    drawStyleRef.current = drawStyle;
    const chart = highChartsRef.current?.chart;
    if (!chart) return;

    const { lineWidth, marker, states } = getLineSeriesStyle(drawStyle);
    chart.series
      .filter(series => series.options.id !== referenceLineId)
      .forEach(series =>
        series.update(
          {
            type: 'line',
            lineWidth,
            states,
            marker: { enabled: marker.enabled, enabledThreshold: marker.enabledThreshold },
          },
          false
        )
      );
    chart.redraw();
  }, [drawStyle, highChartsRef]);

  return drawStyleRef;
};
